/**
 * Filename: AnimationsAndSoundsWorkspace.tsx
 * Description: Reviews animation assets and safely publishes the user-facing seasonal opening selection.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-09-06
 * Updated: 2026-09-07
 */
import React, { useEffect, useState } from 'react';

type AdminApi = (path: string, init?: RequestInit) => Promise<unknown>;
type Configuration = { mode: 'automatic' | 'forced'; themeId: string | null; revision: number; updatedAt: string | null };
const THEMES = [
  ['standard', '🌌 Cosmic Classic'], ['halloween', '🎃 Halloween Night'], ['christmas', '🎄 Christmas Holiday'],
  ['valentines', '💖 Valentine’s Romance'], ['tanabata', '🎋 Tanabata'], ['summerfest', '🎆 Summer Festival'],
  ['childrens', '🎏 Children’s Day'], ['summer', '☀️ Tropical Summer'], ['newyear', '🎍 New Year'],
  ['japanday', '🇯🇵 National Foundation Day'], ['emperors_birthday', '👑 Emperor’s Birthday'],
  ['july4', '🇺🇸 USA & Saipan July 4th'], ['australiaday', '🇦🇺 Australia Day'], ['birthday', '🎂 User Birthday'],
  ['spring', '🌸 Spring Sakura'], ['fall', '🍁 Autumn Foliage'], ['winter', '❄️ Winter Frost'],
  ['chinese_new_year', '🏮 Lunar New Year'], ['thanksgiving', '🦃 Thanksgiving'], ['easter', '🐰 Easter'],
] as const;

export function AnimationsAndSoundsWorkspace({ api, writable }: { api: AdminApi; writable: boolean }) {
  const [published, setPublished] = useState<Configuration>();
  const [mode, setMode] = useState<'automatic' | 'forced'>('automatic');
  const [themeId, setThemeId] = useState('standard');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api('/admin/seasonal-opening').then((payload) => {
      const { configuration } = payload as { configuration: Configuration };
      setPublished(configuration); setMode(configuration.mode); setThemeId(configuration.themeId ?? 'standard');
    }).catch((error) => setNotice(error instanceof Error ? error.message : 'Could not load the published configuration.'));
  }, [api]);

  const publish = async () => {
    const label = mode === 'automatic' ? 'the automatic date schedule' : THEMES.find(([id]) => id === themeId)?.[1] ?? themeId;
    if (!window.confirm(`Publish ${label} to the Shin Q user app? Users will receive this change the next time the app loads.`)) return;
    const reason = window.prompt('Reason for publishing this user-facing animation change:', '')?.trim();
    if (!reason) { setNotice('Publishing canceled: a reason is required.'); return; }
    setBusy(true); setNotice('');
    try {
      const response = await api('/admin/seasonal-opening', { method: 'PATCH', body: JSON.stringify({ mode, themeId: mode === 'forced' ? themeId : null, reason, confirmed: true }) }) as { configuration: Configuration };
      setPublished(response.configuration);
      setNotice('Published. User apps will load this configuration on their next opening.');
    } catch (error) { setNotice(error instanceof Error ? error.message : 'Publishing failed.'); }
    finally { setBusy(false); }
  };

  return <div className="workspace animation-library-workspace">
    <section className="panel animation-library-heading"><div><h2>Animations &amp; sounds</h2><p>Preview locally, then explicitly publish the seasonal opening shown to users.</p></div><a href="/animations-showcase/index.html" rel="noreferrer" target="_blank">Open full screen</a></section>
    <section className="panel">
      <div className="panel-heading"><div><p className="eyebrow">User-facing configuration</p><h3>Published opening theme</h3></div><span className="status-pill">Revision {published?.revision ?? 0}</span></div>
      <p className="muted">Current: {published?.mode === 'forced' ? THEMES.find(([id]) => id === published.themeId)?.[1] : 'Automatic date schedule'}{published?.updatedAt ? ` · ${new Date(published.updatedAt).toLocaleString()}` : ''}</p>
      <div className="admin-create-form">
        <label>Behavior<select disabled={!writable || busy} value={mode} onChange={(event) => setMode(event.target.value as 'automatic' | 'forced')}><option value="automatic">Automatic date schedule</option><option value="forced">Force one theme</option></select></label>
        <label>Theme<select disabled={!writable || busy || mode === 'automatic'} value={themeId} onChange={(event) => setThemeId(event.target.value)}>{THEMES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
        {writable ? <button className="primary" disabled={busy} onClick={() => void publish()}>{busy ? 'Publishing…' : 'Review and publish to users'}</button> : <p className="muted">Your role can preview but cannot publish user-facing changes.</p>}
      </div>
      {notice ? <div className="alert">{notice}</div> : null}
    </section>
    <iframe
      allow="autoplay"
      className="animation-library-frame"
      src="/animations-showcase/index.html?v=20260907-2"
      title="Shin Q animations and sounds library"
      sandbox="allow-downloads allow-modals allow-same-origin allow-scripts"
    />
  </div>;
}
